import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AppHeader } from "../components/AppHeader";
import { Watermark } from "../components/Watermark";
import { apiFetch } from "../lib/api";
import { useMe } from "../hooks/useMe";
import { resolveMembership } from "../hooks/useFamilyMembership";
import { can } from "@family/shared";
import type { Event, EventsListResponse, Membership, MeResponse } from "@family/shared";

function locationLine(e: Event): string {
  const parts = [e.city, e.country].filter(Boolean);
  return parts.join(", ");
}

function formatRange(e: Event): string {
  if (!e.eventDate) {
    return e.dateOptions.length > 0 ? "Date TBA · vote on a date" : "Date TBA";
  }
  const start = new Date(e.eventDate);
  const end = e.endDate ? new Date(e.endDate) : null;
  const fmt = (d: Date) =>
    d.toLocaleDateString("en-GB", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  if (end && e.endDate !== e.eventDate) return `${fmt(start)} – ${fmt(end)}`;
  return fmt(start);
}

export function EventsPage() {
  const meQuery = useMe();
  const primary = resolveMembership(meQuery.data);
  const slug = primary?.familySlug;

  const query = useQuery<EventsListResponse>({
    queryKey: ["events", slug],
    queryFn: () => apiFetch<EventsListResponse>(`/families/${slug}/events`),
    enabled: Boolean(slug),
  });

  if (!primary || !slug) {
    return (
      <>
        <AppHeader />
        <main className="page">
          <p className="text-muted">Loading…</p>
        </main>
      </>
    );
  }

  const canManage = can(primary.role, "events:manage");
  const now = new Date();
  const isUpcoming = (e: Event) =>
    // An event without a date hasn't happened yet.
    !e.eventDate ||
    new Date(e.eventDate) >= now ||
    Boolean(e.endDate && new Date(e.endDate) >= now);
  const all = query.data?.events ?? [];
  // Dated events first (the API returns them newest first), undated last.
  const upcoming = [
    ...all.filter((e) => e.eventDate && isUpcoming(e)),
    ...all.filter((e) => !e.eventDate),
  ];
  const past = all.filter((e) => !isUpcoming(e));

  return (
    <>
      <AppHeader />
      <main className="page stack-lg">
        <div className="row-between">
          <h1>Events</h1>
          {canManage && (
            <Link to="/events/new" style={{ textDecoration: "none" }}>
              <button>+ New event</button>
            </Link>
          )}
        </div>

        {query.isLoading && <p className="text-muted">Loading…</p>}
        {query.error && (
          <p className="text-error">Error: {(query.error as Error).message}</p>
        )}

        <section className="stack">
          <h2>Upcoming</h2>
          {upcoming.length === 0 ? (
            <p className="text-muted">No upcoming events.</p>
          ) : (
            <ul
              className="list-unstyled"
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fill, minmax(280px, 1fr))",
                gap: "var(--s-4)",
              }}
            >
              {upcoming.map((e) => (
                <EventCard key={e.id} event={e} membership={primary} meData={meQuery.data} />
              ))}
            </ul>
          )}
        </section>

        {past.length > 0 && (
          <section className="stack">
            <h2>Past</h2>
            <ul
              className="list-unstyled"
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fill, minmax(280px, 1fr))",
                gap: "var(--s-4)",
              }}
            >
              {past.map((e) => (
                <EventCard key={e.id} event={e} past membership={primary} meData={meQuery.data} />
              ))}
            </ul>
          </section>
        )}
      </main>
    </>
  );
}

function EventCard({ event, past, membership, meData }: { event: Event; past?: boolean; membership?: Membership; meData?: MeResponse }) {
  return (
    <li
      className="card"
      style={{
        padding: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Link
        to={`/events/${event.id}`}
        style={{
          textDecoration: "none",
          color: "var(--c-text)",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {event.coverUrl ? (
          <div
            style={{
              position: "relative",
              aspectRatio: "4 / 3",
              background: "var(--c-bg-alt)",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            <img
              src={event.coverUrl}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
                filter: past ? "grayscale(0.3)" : undefined,
                pointerEvents: "none",
              }}
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
            />
            {membership && meData && (
              <Watermark
                config={membership.watermark}
                variables={{
                  user: meData.user.displayName ?? meData.user.email ?? "",
                  family: membership.familyName,
                  phone: meData.user.phone ?? null,
                  email: meData.user.email ?? null,
                }}
              />
            )}
          </div>
        ) : (
          <div
            style={{
              aspectRatio: "4 / 3",
              background:
                "linear-gradient(135deg, var(--c-bg-alt), var(--c-border))",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--c-text-subtle)",
              fontSize: "0.85rem",
              flexShrink: 0,
            }}
          >
            Event
          </div>
        )}
        <div
          style={{
            padding: "var(--s-4)",
            display: "flex",
            flexDirection: "column",
            gap: 6,
            flex: 1,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: "var(--s-2)",
            }}
          >
            <h3 style={{ fontSize: "1.05rem", margin: 0, lineHeight: 1.3 }}>
              {event.title}
            </h3>
            {event.myRsvp && !past && (
              <span
                className={`badge ${event.myRsvp === "yes" ? "badge--admin" : ""}`}
                style={{ flexShrink: 0 }}
              >
                {event.myRsvp}
              </span>
            )}
          </div>
          <div
            className="text-muted"
            style={{ fontSize: "0.88rem", marginTop: "auto" }}
          >
            {formatRange(event)}
            {locationLine(event) && ` · ${locationLine(event)}`}
          </div>
        </div>
      </Link>
    </li>
  );
}
